<template>
  <div :class="$style.projectCriteriaEditContainer">
    <p
      v-if="
        isUserProjectOwnerOrCoordinator &&
        !isProjectCriteriaAcknowledged &&
        isJbiCriteriaUpdated
      "
      class="criteria-tab-text text-size"
    >
      Your JBI criteria have been updated, please view and acknowledge the
      change(s)
      <a @click="openJBICriteriaAcknowledgmentModal" :class="$style.clickBtn">
        here</a
      >.
    </p>
    <p
      :class="[
        !isProjectCriteriaAcknowledged && isJbiCriteriaUpdated
          ? $style.criteriaInfo
          : 'criteria-tab-text',
        `text-size`,
      ]"
    >
      Setup Criteria for project-wide here. You may also customise criteria for
      each data collection period. Any changes here will be applied to the new
      data collection period.
    </p>
    <AuditCriteriaList
      v-if="selectedCriteriaList.length > 0"
      :selectedCriteriaArray.sync="selectedCriteriaList"
      :projectEdit="true"
      :projectId="projectId"
      :editable="editable"
      :isProject="true"
    />
    <template v-if="editable">
      <div class="columns" :class="$style.addItem">
        <div class="column">
          <a
            @click="openJBICriteriaSelectionTemplate"
            class="add-participant-text"
          >
            + Add Criteria
          </a>
        </div>
      </div>
      <div class="columns">
        <div class="column is-two-thirds vertical-center text-color"></div>
        <div class="column is-one-third save-btn-container">
          <b-button
            :disabled="isAuditCriteriaSubmitButtonDisabled"
            @click="updateProjectCriteriaHandler"
            :class="$style.saveChangesBtn"
            type="is-primary"
            >Save
          </b-button>
        </div>
      </div>
    </template>
    <JBICriteriaAcknowledgmentModal
      v-if="isModalActive"
      :isModalActive="isModalActive"
      :isMinimized="isMinimized"
      :projectId="projectId"
      :updatedJBICriteriaResponse="updatedJBICriteriaResponseForModal"
      :projectCriteriaAcknowledgement="projectCriteriaAcknowledgementForModal"
      @close="closeJBICriteriaAcknowledgmentModal"
      @minimize="minimizeJBICriteriaAcknowledgmentModal"
      @restore="restoreJBICriteriaAcknowledgmentModal"
    />
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import AuditCriteriaList from '@/views/Project/components/AuditCriteriaList.vue';
import { Action, namespace, State } from 'vuex-class';
import {
  ProjectCriteria,
  CplusCriteria,
  UpdateProjectCriteriaRequestPayload,
  ProjectDetailPayload,
  ProjectParticipantDTO,
  UpdateCriteriaDTO,
  UpdatedJBICriteriaResponse,
  ProjectCriteriaAcknowledgement,
} from '@/store/modules/projects/types/projects.types';
import {
  isDifferentDeep,
  isTruthy,
} from '@/jbi-shared/util/watcher.vue-decorator';
import { isEqual as _isEqual } from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import JBICriteriaAcknowledgmentModal from '../components/JBICriteriaAcknowledgmentModal.vue';
import {
  JBICriteriaDTO,
  UpdatedJBICriteriaAcknowledgement,
} from '../../../store/modules/projects/types/projects.types';
import { ApiState } from '../../../store/types/general.types';
import JBICriteriaSelection from '@/views/Audit/components/JBICriteriaSelection.vue';
import { useUserEmail } from '@/utils/user.util';
import { transformCriteriaIntoCplusDTO } from '@/utils/criteria.util';
import { AuditStatus } from '@/store/modules/audits/types/audits.types';
import { RootState } from '@/store/store';

const unsavedChangesGuard = namespace('unsavedChangesGuard');

@Component({
  components: {
    JBICriteriaSelection,
    AuditCriteriaList,
    JBICriteriaAcknowledgmentModal,
  },
})
export default class ProjectCriteriaTab extends Vue {
  @Prop({ default: true })
  public editable!: boolean;
  @Prop({ default: false })
  public isUserProjectOwnerOrCoordinator!: boolean;
  @Prop()
  public projectId!: number;
  @Prop()
  public projectDetail!: ProjectDetailPayload;
  public selectedCriteriaArray: CplusCriteria[] = [];
  public isAuditCriteriaSubmitButtonDisabled = true;
  public dirtyCriteriaArray: CplusCriteria[] = [];
  public deletedCriteriaList: number[] = [];
  public updatedJbiCriteriaList: JBICriteriaDTO[] = [];
  public selectedAuditCycleIds: number[] = [];
  public isProjectCriteriaAcknowledged: boolean = true;

  public isModalActive: boolean = false;
  public isMinimized: boolean = false;

  @Action('projects/getProjectCriteria')
  public getProjectCriteria!: (projectId: number) => void;

  @State((state) => state.projects.projectCriteria)
  public projectCriteria!: ProjectCriteria[];

  @State((state: RootState) => state.projects.apiState.getProjectCriteria)
  public projectCriteriaState!: ProjectCriteria[];

  @Action('projects/updateProjectCriterias')
  public updateProjectCriterias!: (
    payload: UpdateProjectCriteriaRequestPayload,
  ) => void;

  @State((state) => state.projects.updatedProjectCriterias)
  public updatedProjectCriterias!: any;

  @Action('projects/getUpdatedJBICriteriaWithAcknowledgement')
  public getUpdatedJBICriteriaWithAcknowledgement!: (projectId: number) => void;

  @State((state) => state.projects.updatedJBICriteriaListWithAcknowledgment)
  public updatedJBICriteriaListWithAcknowledgment!: UpdatedJBICriteriaAcknowledgement;

  @State(
    (state) =>
      state.projects.apiState.getUpdatedJBICriteriaListWithAcknowledgment,
  )
  public getUpdatedJBICriteriaListWithAcknowledgmentState!: ApiState;

  @unsavedChangesGuard.State('userSelectedKeepEditing')
  public userSelectedKeepEditing!: boolean;

  @unsavedChangesGuard.Action('resetKeepEditingFlag')
  public resetKeepEditingFlag!: () => void;

  public mounted() {
    this.getProjectCriteria(this.getProjectId),
      this.getUpdatedJBICriteriaWithAcknowledgement(this.getProjectId);
  }

  get userEmail(): string | undefined {
    return useUserEmail.call(this);
  }

  get getProjectId(): number {
    return this.projectId;
  }

  get selectedCriteriaList() {
    return this.selectedCriteriaArray;
  }

  set selectedCriteriaList(newValue: CplusCriteria[]) {
    this.selectedCriteriaArray = newValue;
  }

  get isJbiCriteriaUpdated(): boolean {
    return (
      this.updatedJBICriteriaListWithAcknowledgment &&
      (this.updatedJBICriteriaListWithAcknowledgment.newJBICriteria.length >
        0 ||
        this.updatedJBICriteriaListWithAcknowledgment.updatedJBICriteria
          .length > 0 ||
        this.updatedJBICriteriaListWithAcknowledgment.deletedJBICriteria
          .length > 0)
    );
  }

  get updatedJBICriteriaForExistingCriteria(): JBICriteriaDTO[] {
    return this.updatedJBICriteriaListWithAcknowledgment
      ? [
          ...this.updatedJBICriteriaListWithAcknowledgment.updatedJBICriteria,
          ...this.updatedJBICriteriaListWithAcknowledgment.deletedJBICriteria,
        ]
      : [];
  }

  get updatedJBICriteriaResponseForModal(): UpdatedJBICriteriaResponse | null {
    if (!this.updatedJBICriteriaListWithAcknowledgment) return null;
    const {
      projectCriteriaAcknowledgement,
      ...response
    } = this.updatedJBICriteriaListWithAcknowledgment;
    return response;
  }

  get projectCriteriaAcknowledgementForModal(): ProjectCriteriaAcknowledgement | null {
    return this.updatedJBICriteriaListWithAcknowledgment
      ? this.updatedJBICriteriaListWithAcknowledgment
          .projectCriteriaAcknowledgement
      : null;
  }

  public openJBICriteriaAcknowledgmentModal(): void {
    this.isModalActive = true;
    this.isMinimized = false; // Ensure it opens in a restored state
  }

  public closeJBICriteriaAcknowledgmentModal(): void {
    this.isModalActive = false;
    this.isMinimized = false;
  }

  public minimizeJBICriteriaAcknowledgmentModal(): void {
    this.isMinimized = true;
  }

  public restoreJBICriteriaAcknowledgmentModal(): void {
    this.isMinimized = false;
  }

  public transformCriterias(criterias: any[]): void {
    if (!criterias) {
      return;
    }

    this.selectedCriteriaArray = transformCriteriaIntoCplusDTO(
      criterias,
      this.updatedJbiCriteriaList,
      true,
    );
  }

  @Watch('projectCriteriaState')
  @isTruthy
  public watchProjectCriteriaState(state: ApiState): void {
    if (state.success) {
      this.transformCriterias(this.projectCriteria);
    }
  }

  @Watch('getUpdatedJBICriteriaListWithAcknowledgmentState')
  @isTruthy
  public watchUpdatedJbiCriteriaResponse(): void {
    this.updatedJbiCriteriaList = this.updatedJBICriteriaForExistingCriteria;
    this.transformCriterias(this.projectCriteria);

    if (
      this.updatedJBICriteriaListWithAcknowledgment &&
      this.updatedJBICriteriaListWithAcknowledgment
        .projectCriteriaAcknowledgement
    ) {
      // tslint:disable-next-line:max-line-length
      this.isProjectCriteriaAcknowledged = this.updatedJBICriteriaListWithAcknowledgment.projectCriteriaAcknowledgement.isAcknowledged;
    }
  }

  @Watch('selectedCriteriaArray')
  @isTruthy
  @isDifferentDeep
  public watchSelectedCriteriaArray(newValue: any) {
    this.isAuditCriteriaSubmitButtonDisabled = _isEqual(
      this.dirtyCriteriaArray,
      newValue,
    );
  }

  @Watch('updatedProjectCriterias')
  @isTruthy
  public watchUpdatedProjectCriterias() {
    this.getProjectCriteria(this.getProjectId);
    this.getUpdatedJBICriteriaWithAcknowledgement(this.getProjectId);
    Toast.open({
      message: `Criteria successfully updated.`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  @Watch('userSelectedKeepEditing')
  public onUserSelectedKeepEditing(newValue: boolean): void {
    if (newValue) {
      this.resetKeepEditingFlag();
      if (this.isMinimized && this.isModalActive) {
        this.restoreJBICriteriaAcknowledgmentModal();
      }
    }
  }

  public openJBICriteriaSelectionTemplate() {
    this.$buefy.modal.open({
      parent: this,
      component: JBICriteriaSelection,
      fullScreen: true,
      hasModalCard: false,
      trapFocus: true,
      animation: 'unset',
      props: {
        projectId: this.getProjectId,
        projectName: this.getProjectTitle,
        isProjectLevel: true,
        selectedCriteriaArray: this.selectedCriteriaArray,
      },
      events: {
        updateSelectedCriteriaArray: (newValue: UpdateCriteriaDTO) => {
          this.selectedCriteriaArray = newValue.emitCriteriaArray;
          this.deletedCriteriaList = newValue.deletedCriteriaIds;
          this.selectedAuditCycleIds = newValue.selectedAuditCycleIds;
          this.updateProjectCriteriaHandler();
        },
      },
    });
  }

  get getProjectTitle(): string {
    return this.projectDetail.title;
  }

  public updateProjectCriteriaHandler() {
    this.updateProjectCriterias({
      projectId: this.getProjectId,
      criteriaList: this.selectedCriteriaArray,
      selectedAuditCycleIds: this.selectedAuditCycleIds,
      deletedCriteriaIds: this.deletedCriteriaList,
    });
  }
}
</script>
<style lang="scss" module>
.projectCriteriaEditContainer {
  padding: 0rem 11rem;
}

.addItem {
  color: #21b5ea;
  font-weight: 400;
}

.saveChangesBtn {
  float: right;
  border-radius: 24px;
  height: 48px;
  width: auto;
  padding: 0 calc(1em + 0.25em);
}
.updatedJbiCriteriaNotification {
  background-color: #eff7ff !important;
  div > div {
    color: #444f51;
  }
}
.criteriaInfo {
  padding: 0 16px 0 16px;
}
.updateAvailableLable {
  font-weight: bold;
}
.clickBtn {
  color: #00205b;
  font-weight: bold;
}
</style>
